import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import type {
  McpAdminGrantView,
  McpConsentApproveBody,
  McpConsentDecisionResult,
  McpConsentDenyBody,
  McpConsentDetails,
  McpGrantList,
  McpGrantView,
  McpRevokeResult,
} from "@shared/mcp/consent";
import { holdsMcpWritePermission } from "@shared/mcp/consent";
import { MCP_SCOPE_WRITE } from "@shared/mcp/constants";
import type {
  McpInstanceSwitchView,
  McpTenantSwitchView,
  PutMcpInstanceSwitchBody,
  PutMcpTenantSwitchBody,
} from "@shared/mcp/switches";
import type { McpWriteIntentDecisionResult, McpWriteIntentView } from "@shared/mcp/writeIntents";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { apiRequest } from "@/lib/api";

/**
 * AI apps over MCP (ADR 0009): the consent page, "Connected apps", the admins' grant lists, the
 * revocations and the switches. Every path is the server's (`server/routes/mcpGrants.ts`,
 * `server/routes/mcpSwitches.ts`); the shapes are `shared/mcp/`. Query keys start with the path,
 * as the default query function fetches `queryKey[0]`.
 */

export const MCP_CONSENT_API_PATH = "/api/v2/mcp/consent";
export const MY_MCP_GRANTS_PATH = "/api/v2/mcp/grants";
export const INSTANCE_MCP_GRANTS_PATH = "/api/v2/instances/current/mcp/grants";
export const INSTANCE_MCP_SWITCH_PATH = "/api/v2/instances/current/mcp/switch";
export const MCP_REVOKE_ALL_PATH = "/api/v2/platform/mcp/revoke-all";
/** What the PlatformAdmin types to confirm revoke-all; the server refuses anything else. */
export const MCP_REVOKE_ALL_CONFIRMATION = "REVOKE ALL";

/** The approve page's API (ADR 0009 section 6, release 8B): `server/routes/mcpWriteIntents.ts`. */
export const MCP_WRITE_INTENTS_PATH = "/api/v2/mcp/write-intents";

export function mcpWriteIntentPath(id: string): string {
  return `${MCP_WRITE_INTENTS_PATH}/${encodeURIComponent(id)}`;
}

/** While an approved write runs, the page reads its outcome again this often. */
const RUNNING_WRITE_POLL_MS = 2_000;

export function tenantMcpGrantsPath(tenantId: string): string {
  return `/api/v2/tenants/${tenantId}/mcp/grants`;
}

export function tenantMcpSwitchPath(tenantId: string): string {
  return `/api/v2/tenants/${tenantId}/mcp/switch`;
}

export function mcpConsentDetailsPath(ref: string): string {
  return `${MCP_CONSENT_API_PATH}?request=${encodeURIComponent(ref)}`;
}

/** `features.flags.mcp`, from `GET /me`: off, "Connected apps" and the AI apps page are hidden. */
export function useMcpEnabled(): boolean {
  return useTenantInstance().me?.features?.mcp === true;
}

/**
 * What the consent page shows, in the selected instance. Never retried: a reference that is bad
 * or expired stays bad, and the page says to start again from the AI app.
 */
export function useMcpConsentDetails(
  ref: string | null,
  instanceId: string | null,
): UseQueryResult<McpConsentDetails> {
  return useQuery<McpConsentDetails>({
    // The instance is in the key: the details (and the grant to be made) follow the instance.
    queryKey: [ref ? mcpConsentDetailsPath(ref) : "mcp-consent-disabled", instanceId],
    enabled: Boolean(ref && instanceId),
    retry: false,
    // The answer carries `needsRecentSignIn`, which a sign-in changes: always read it fresh.
    staleTime: 0,
    gcTime: 0,
  });
}

export function useApproveMcpConsent() {
  return useMutation({
    mutationFn: (body: McpConsentApproveBody) =>
      apiRequest<McpConsentDecisionResult>(`${MCP_CONSENT_API_PATH}/approve`, {
        method: "POST",
        body,
      }),
  });
}

export function useDenyMcpConsent() {
  return useMutation({
    mutationFn: (body: McpConsentDenyBody) =>
      apiRequest<McpConsentDecisionResult>(`${MCP_CONSENT_API_PATH}/deny`, {
        method: "POST",
        body,
      }),
  });
}

/** The signed-in person's own grants, every instance ("Connected apps"). */
export function useMyMcpGrants(enabled = true): UseQueryResult<McpGrantList<McpGrantView>> {
  return useQuery<McpGrantList<McpGrantView>>({ queryKey: [MY_MCP_GRANTS_PATH], enabled });
}

/**
 * Whether Connected apps speaks of change as well as read (#1393 finding 61): `true` when the
 * person holds a write permission in any instance (`holdsMcpWritePermission`, what the consent
 * page offers) or any listed connection still carries `skydata.write`, since lowering a role ends
 * no grant and its row reads "Read and change"; `false` only when neither holds once both are
 * read; `undefined` while either is unknown, so the full wording stands. The subtitle
 * (`App.tsx`) and the page's own paragraph (`pages/connected-apps.tsx`) read this one rule.
 */
export function useMcpWriter(enabled = true): boolean | undefined {
  const { me } = useTenantInstance();
  const grants = useMyMcpGrants(enabled);
  if (!me) return undefined;
  if (me.instances.some((instance) => holdsMcpWritePermission(instance.permissions))) return true;
  if (!grants.data) return undefined;
  return grants.data.grants.some((grant) => grant.scopes.includes(MCP_SCOPE_WRITE));
}

export function useInstanceMcpGrants(
  enabled = true,
): UseQueryResult<McpGrantList<McpAdminGrantView>> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<McpGrantList<McpAdminGrantView>>({
    queryKey: [INSTANCE_MCP_GRANTS_PATH, selectedInstance?.id ?? null],
    enabled: enabled && Boolean(selectedInstance),
  });
}

export function useTenantMcpGrants(
  tenantId: string | null,
  enabled = true,
): UseQueryResult<McpGrantList<McpAdminGrantView>> {
  return useQuery<McpGrantList<McpAdminGrantView>>({
    queryKey: [tenantId ? tenantMcpGrantsPath(tenantId) : "mcp-tenant-grants-disabled"],
    enabled: enabled && Boolean(tenantId),
  });
}

/** Every grant list may show a grant a revoke ended: refresh them all. */
function useInvalidateGrantLists() {
  const client = useQueryClient();
  return () =>
    client.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === "string" && key.includes("/mcp/grants");
      },
    });
}

/** Which list a revoke goes through: the person's own, the instance's or the tenant's. */
export type McpRevokeScope =
  { kind: "mine" } | { kind: "instance" } | { kind: "tenant"; tenantId: string };

export function revokePathFor(scope: McpRevokeScope, grantId: string): string {
  if (scope.kind === "mine") return `${MY_MCP_GRANTS_PATH}/${grantId}`;
  if (scope.kind === "instance") return `${INSTANCE_MCP_GRANTS_PATH}/${grantId}`;
  return `${tenantMcpGrantsPath(scope.tenantId)}/${grantId}`;
}

export function useRevokeMcpGrant(scope: McpRevokeScope) {
  const invalidate = useInvalidateGrantLists();
  return useMutation({
    mutationFn: (grantId: string) =>
      apiRequest<McpRevokeResult>(revokePathFor(scope, grantId), { method: "DELETE" }),
    onSettled: () => invalidate(),
  });
}

export function useRevokeAllMcpGrants() {
  const invalidate = useInvalidateGrantLists();
  return useMutation({
    mutationFn: () =>
      apiRequest<McpRevokeResult>(MCP_REVOKE_ALL_PATH, {
        method: "POST",
        body: { confirm: MCP_REVOKE_ALL_CONFIRMATION },
      }),
    onSettled: () => invalidate(),
  });
}

export function useTenantMcpSwitch(
  tenantId: string | null,
  enabled = true,
): UseQueryResult<McpTenantSwitchView> {
  return useQuery<McpTenantSwitchView>({
    queryKey: [tenantId ? tenantMcpSwitchPath(tenantId) : "mcp-tenant-switch-disabled"],
    enabled: enabled && Boolean(tenantId),
  });
}

export function useSetTenantMcpSwitch(tenantId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PutMcpTenantSwitchBody) =>
      apiRequest<McpTenantSwitchView>(tenantMcpSwitchPath(tenantId ?? ""), {
        method: "PUT",
        body,
        // #1110: turning AI apps on or off needs a recent sign-in.
        stepUp: "change",
      }),
    onSuccess: (view) => {
      client.setQueryData([tenantMcpSwitchPath(view.tenantId)], view);
      // The instance switch shows what the tenant allows.
      void client.invalidateQueries({ queryKey: [INSTANCE_MCP_SWITCH_PATH] });
    },
  });
}

export function useInstanceMcpSwitch(enabled = true): UseQueryResult<McpInstanceSwitchView> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<McpInstanceSwitchView>({
    queryKey: [INSTANCE_MCP_SWITCH_PATH, selectedInstance?.id ?? null],
    enabled: enabled && Boolean(selectedInstance),
  });
}

export function useSetInstanceMcpSwitch() {
  const client = useQueryClient();
  const { selectedInstance } = useTenantInstance();
  return useMutation({
    mutationFn: (body: PutMcpInstanceSwitchBody) =>
      apiRequest<McpInstanceSwitchView>(INSTANCE_MCP_SWITCH_PATH, {
        method: "PUT",
        body,
        // #1110: turning AI apps on or off needs a recent sign-in.
        stepUp: "change",
      }),
    onSuccess: (view) => {
      client.setQueryData([INSTANCE_MCP_SWITCH_PATH, selectedInstance?.id ?? null], view);
    },
  });
}

/**
 * The write an AI app asks the person to approve (`GET /api/v2/mcp/write-intents/:id`), in the
 * selected instance: the server answers 404 unless the intent is this person's, in this
 * instance. `enabled` is false while the link's `?instance=` waits for "Switch", so nothing is
 * read under the wrong instance. Never retried (a 404 stays a 404); read again every 2 seconds
 * while an approved write is still running.
 */
export function useMcpWriteIntent(
  id: string,
  enabled: boolean,
): UseQueryResult<McpWriteIntentView> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<McpWriteIntentView>({
    queryKey: [mcpWriteIntentPath(id), selectedInstance?.id ?? null],
    enabled: enabled && Boolean(selectedInstance),
    retry: false,
    staleTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "executing" ? RUNNING_WRITE_POLL_MS : false;
    },
  });
}

/**
 * Approve or decline (`POST …/:id/approve`, `…/:id/decline`, an empty body). Both answer the
 * intent as it now stands, which replaces the cached one.
 */
export function useDecideMcpWriteIntent(id: string, decision: "approve" | "decline") {
  const client = useQueryClient();
  const { selectedInstance } = useTenantInstance();
  return useMutation({
    mutationFn: () =>
      apiRequest<McpWriteIntentDecisionResult>(`${mcpWriteIntentPath(id)}/${decision}`, {
        method: "POST",
        body: {},
        // #1110: approving runs the AI app's write, so it needs a recent sign-in (decline does not).
        ...(decision === "approve" ? { stepUp: "change" as const } : {}),
      }),
    onSuccess: (result) => {
      // A write still running is read again by `useMcpWriteIntent`'s interval.
      client.setQueryData([mcpWriteIntentPath(id), selectedInstance?.id ?? null], result.intent);
    },
    onError: () => {
      // A refusal may still have moved the intent (an approval the grant could no longer run is
      // recorded `failed` before `MCP_GRANT_ENDED` answers): read it again, so the page shows
      // what is recorded instead of offering the decision a second time.
      void client.invalidateQueries({
        queryKey: [mcpWriteIntentPath(id), selectedInstance?.id ?? null],
      });
    },
  });
}
